import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { History, ArrowRight } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
}

export default function FeeHistoryDialog({ open, onOpenChange, student }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !student?.id) return;
    setLoading(true);
    api
      .get(`/students/${student.id}/fee-history`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open, student]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto border-2 border-[#0A0A0A] shadow-brutal rounded-xl bg-white"
        data-testid="fee-history-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2.5 border-2 border-[#0A0A0A] rounded-xl bg-[#00E5FF]">
              <History size={22} strokeWidth={2.5} />
            </div>
            <div>
              <DialogTitle className="font-heading font-black text-2xl tracking-tighter">
                Fee Change History
              </DialogTitle>
              <DialogDescription className="text-sm font-medium text-[#4A4A4A]">
                Every part-payment, discount, and fee edit for{" "}
                <span className="font-bold">{student?.full_name}</span>.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="mt-4 space-y-3">
          {loading && (
            <div className="p-6 text-center font-medium text-[#4A4A4A]">Loading...</div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-6 text-center font-medium text-[#4A4A4A] border-2 border-dashed border-[#0A0A0A] rounded-xl">
              No fee changes recorded yet.
            </div>
          )}
          {items.map((c) => {
            const isPayment = c.field === "fees_paid" && c.delta > 0;
            const isDiscount = c.field === "discount_amount";
            const tone = isDiscount
              ? { bg: "#FFE600", fg: "#0A0A0A" }
              : isPayment
              ? { bg: "#00CC66", fg: "#FFFFFF" }
              : { bg: "#FF4D4D", fg: "#FFFFFF" };

            return (
              <div
                key={c.id}
                data-testid={`fee-history-row-${c.id}`}
                className="border-2 border-[#0A0A0A] rounded-xl p-4 bg-white"
                style={{ boxShadow: "3px 3px 0px 0px rgba(10,10,10,1)" }}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-xs font-bold uppercase tracking-widest px-2.5 py-1 border-2 border-[#0A0A0A] rounded-full"
                      style={{ backgroundColor: tone.bg, color: tone.fg }}
                    >
                      {c.label}
                    </span>
                    <span className="text-xs font-medium text-[#4A4A4A]">
                      by {c.changed_by_name || c.changed_by || "Admin"}
                    </span>
                  </div>
                  <span
                    className="text-xs font-bold text-[#4A4A4A]"
                    data-testid={`fee-history-date-${c.id}`}
                  >
                    {fmtDate(c.changed_at)}
                  </span>
                </div>
                <div className="mt-3 flex items-center gap-3 font-heading font-black text-xl tracking-tighter">
                  <span className="text-[#4A4A4A] line-through">
                    ₹{Number(c.old_value).toLocaleString()}
                  </span>
                  <ArrowRight size={18} strokeWidth={3} />
                  <span>₹{Number(c.new_value).toLocaleString()}</span>
                  <span
                    className={`ml-2 text-sm px-2 py-0.5 border-2 border-[#0A0A0A] rounded-full ${
                      c.delta >= 0 ? "bg-[#00CC66] text-white" : "bg-[#FF4D4D] text-white"
                    }`}
                  >
                    {c.delta >= 0 ? "+" : ""}
                    ₹{Number(c.delta).toLocaleString()}
                  </span>
                </div>
                {c.note && (
                  <div className="mt-3 text-sm font-medium bg-[#FFFCE0] border-2 border-[#0A0A0A] rounded-lg p-2.5">
                    Note: {c.note}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
